import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  IconButton,
  Paper,
  Alert,
  Avatar,
  Stack,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Engineering as EngineeringIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  LocalShipping as TransportIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as EmojiEventsIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedUserIcon,
  HowToReg as HowToRegIcon,
  AssignmentInd as AssignmentIndIcon,
  WhatsApp,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { color } from "framer-motion";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "incomplete",
})(({ theme, incomplete }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 28px rgba(0,0,0,0.12)",
  },
  ...(incomplete && {
    borderLeft: `6px solid #f44336`,
    backgroundColor: "#fff5f5",
    "&:hover": {
      boxShadow: "0 6px 28px rgba(244,67,54,0.2)",
    },
  }),
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
  fontWeight: 700,
  fontSize: "0.9rem",
  padding: "4px 8px",
  height: "auto",
  "& .MuiChip-icon": {
    fontSize: "1.2rem",
  },
  ...(statuscolor === "completed" && {
    backgroundColor: "#4caf50",
    color: "white",
    "&:hover": { backgroundColor: "#45a049" },
  }),
  ...(statuscolor === "in-progress" && {
    backgroundColor: "#ff9800",
    color: "white",
    "&:hover": { backgroundColor: "#fb8c00" },
  }),
  ...(statuscolor === "pending" && {
    backgroundColor: "#9e9e9e",
    color: "white",
    "&:hover": { backgroundColor: "#757575" },
  }),
}));

const StatusIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "color",
})(({ theme, active, color }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  backgroundColor: active ? `${color}15` : theme.palette.background.paper,
  border: `1px solid ${active ? color : theme.palette.divider}`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  textAlign: "center",
  height: "100%",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}));

const CompletionRateCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "rate",
})(({ theme, rate }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  textAlign: "center",
  background:
    rate >= 80
      ? "linear-gradient(135deg, #4caf50 0%, #45a049 100%)"
      : rate >= 50
        ? "linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)"
        : "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
  color: "white",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.02)",
  },
}));

const IncompleteWarning = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: "#ffebee",
  borderRadius: theme.spacing(1),
  borderLeft: `4px solid #f44336`,
  marginBottom: theme.spacing(2),
}));

const PersonnelCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: "#f8f9fa",
  transition: "all 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    backgroundColor: "#ffffff",
  },
}));

const LabRequestProgress = ({ labRequestData }) => {
  const [expandedTests, setExpandedTests] = useState(new Set());

  const filteredTests = useMemo(() => {
    if (!labRequestData?.labRequestTests) return [];
    return labRequestData.labRequestTests.filter(
      (test) =>
        test.labTest?.testName !== "Transport  (per 1 Km)" &&
        test.labTest?.category?.category !== "Transport",
    );
  }, [labRequestData?.labRequestTests]);

  if (!labRequestData || !labRequestData.labRequestTests) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No lab request data available
        </Typography>
      </Paper>
    );
  }

  const allNonTransportTests = filteredTests;
  const completionRate =
    allNonTransportTests.length > 0
      ? Math.round(
          (allNonTransportTests.filter((t) => t.checkedByEngineer).length /
            allNonTransportTests.length) *
            100,
        )
      : 0;

  const handleAccordionToggle = (testId) => {
    setExpandedTests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testId)) {
        newSet.delete(testId);
      } else {
        newSet.add(testId);
      }
      return newSet;
    });
  };

  const calculateProgress = (test) => {
    let steps = 0;
    if (test.finalized) steps++;
    if (test.checked) steps++;
    if (test.checkedByEngineer) steps++;
    return (steps / 3) * 100;
  };

  const getStatusInfo = (test) => {
    if (test.checkedByEngineer) {
      return {
        label: "Engineer Approved",
        color: "#4caf50",
        icon: <CheckCircleIcon />,
        status: "completed",
        bgColor: "#e8f5e9",
      };
    } else if (test.checked) {
      return {
        label: "TO Checked",
        color: "#2196f3",
        icon: <AssignmentIcon />,
        status: "in-progress",
        bgColor: "#e3f2fd",
      };
    } else if (test.finalized) {
      return {
        label: "Finalized",
        color: "#ff9800",
        icon: <ScienceIcon />,
        status: "in-progress",
        bgColor: "#fff3e0",
      };
    } else {
      return {
        label: "Pending",
        color: "#9e9e9e",
        icon: <PendingIcon />,
        status: "pending",
        bgColor: "#f5f5f5",
      };
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "Not yet";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const getStepIcon = (step, isCompleted) => {
    if (isCompleted) {
      return <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 28 }} />;
    }
    switch (step) {
      case 1:
        return <ScienceIcon sx={{ color: "#ff9800", fontSize: 28 }} />;
      case 2:
        return <AssignmentIcon sx={{ color: "#2196f3", fontSize: 28 }} />;
      case 3:
        return <EngineeringIcon sx={{ color: "#9c27b0", fontSize: 28 }} />;
      default:
        return <PendingIcon sx={{ color: "#9e9e9e", fontSize: 28 }} />;
    }
  };

  // Helper function to get user display name from user object
  const getUserDisplayName = (user) => {
    if (!user) return "Not Assigned";
    // If user is an object with firstName and lastName
    if (typeof user === "object") {
      const firstName = user.firstName || user.first_name || "";
      const lastName = user.lastName || user.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
      // Fallback to username or email if available
      return user.username || user.email || "Not Assigned";
    }
    // If user is a string, return it directly
    if (typeof user === "string") return user;
    return "Not Assigned";
  };

  // Helper function to get user position from user object
  const getUserPosition = (user) => {
    if (!user) return "";
    // if (typeof user === "object") {
    //   return user.position || user.role || user.designation || "";
    // }
    return "";
  };

  // Helper function to safely render user info
  const renderUserInfo = (user, icon) => {
    if (!user) {
      return (
        <Typography variant="body2" color="textSecondary">
          Not assigned yet
        </Typography>
      );
    }

    const displayName = getUserDisplayName(user);
    const position = getUserPosition(user);

    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {icon}
          <Typography variant="body2" fontWeight="bold" color="textSecondary">
            {getUserTypeLabel(user)}
          </Typography>
        </Box>
        <Typography variant="body1" fontWeight="bold">
          {displayName}
        </Typography>
        {position && (
          <Typography variant="caption" color="textSecondary">
            {position}
          </Typography>
        )}
      </>
    );
  };

  const getUserTypeLabel = (user) => {
    if (!user) return "Unknown";
    // You can add logic here to determine the user type based on properties
    if (user.position === "Engineer") return "Engineer";
    if (user.position === "Technical Officer") return "Technical Officer";
    if (user.role === "admin") return "Administrator";
    return "Staff";
  };

  const isPaid = labRequestData?.paid || false;
  const paymentMethod = labRequestData.labRequest?.paymentMethod;
  const paymentDate = labRequestData.labRequest?.paymentDate;
  const grandTotal = labRequestData?.grandTotal || 0;
  const balance = labRequestData?.balance || 0;

  // Count incomplete tests (not engineer approved)
  const incompleteTestsCount = allNonTransportTests.filter(
    (t) => !t.checkedByEngineer,
  ).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, margin: "0 auto" }}>
      {/* Header Section with Job Info */}
      <Card
        sx={{
          mb: 4,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{ color: "white", fontWeight: "bold", mb: 2 }}
          >
            Lab Request / Job Progress
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 1, fontWeight: 500 }}
              >
                <strong>Job No:</strong>{" "}
                {`${labRequestData.year}/${String(labRequestData.autoIdByYear).padStart(4, "0")}`}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500 }}
              >
                <strong>Project:</strong> {labRequestData?.project || "N/A"}
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 1, fontWeight: 500 }}
              >
                <strong>Reference:</strong>{" "}
                {labRequestData.reference || labRequestData?.reference || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500 }}
              >
                <strong>Contact Person:</strong>{" "}
                {labRequestData?.contactPerson || "N/A"}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 1, fontWeight: 500 }}
              >
                <strong>Barcode:</strong>{" "}
                {labRequestData.barcode ||
                  labRequestData.labRequest?.barcode ||
                  "N/A"}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 1, fontWeight: 500 }}
              >
                <strong>Status:</strong>{" "}
                {labRequestData.finalized ? "Finalized" : "In Progress"}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 1, fontWeight: 500 }}
              >
                <strong> Moblie No: {labRequestData.mobileNo}</strong>
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 1, fontWeight: 500 }}
              >
                <strong>WhasApp No: {labRequestData.whatsAppNo}</strong>{" "}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Key Information Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard elevation={2}>
            <CalendarIcon sx={{ fontSize: 48, color: "#667eea", mb: 1 }} />
            <Typography variant="body1" color="textSecondary" fontWeight={600}>
              Job Created
            </Typography>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 1, fontSize: "1.1rem" }}
            >
              {formatDate(labRequestData?.regDate || labRequestData.date)}
            </Typography>
          </InfoCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard elevation={2}>
            <PaymentIcon
              sx={{
                fontSize: 48,
                color: isPaid ? "#4caf50" : "#ff9800",
                mb: 1,
              }}
            />
            <Typography variant="body1" color="textSecondary" fontWeight={600}>
              Payment Status
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: isPaid ? "#4caf50" : "#ff9800", mt: 1 }}
            >
              {isPaid ? "Paid ✓" : "Pending"}
            </Typography>
            {paymentMethod && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 0.5 }}
              >
                Method: {paymentMethod}
              </Typography>
            )}
            {paymentDate && (
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
              >
                Date: {formatDate(paymentDate)}
              </Typography>
            )}
          </InfoCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard elevation={2}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ color: "#2196f3", mb: 1 }}
            >
              Rs. {grandTotal.toLocaleString()}
            </Typography>
            <Typography variant="body1" color="textSecondary" fontWeight={600}>
              Total Amount
            </Typography>
            {balance > 0 && (
              <Typography
                variant="h5"
                color="error"
                sx={{ mt: 1, fontWeight: 600 }}
              >
                Balance: Rs. {balance.toLocaleString()}
              </Typography>
            )}
          </InfoCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CompletionRateCard elevation={2} rate={completionRate}>
            <EmojiEventsIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h3" fontWeight="bold">
              {completionRate}%
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              Completion Rate
            </Typography>
            <Typography
              variant="caption"
              sx={{ mt: 1, display: "block", opacity: 0.9 }}
            >
              {allNonTransportTests.filter((t) => t.checkedByEngineer).length} /{" "}
              {allNonTransportTests.length} Tests
            </Typography>
          </CompletionRateCard>
        </Grid>
      </Grid>

      {/* Incomplete Tests Summary Banner */}
      {incompleteTestsCount > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: "#fff3e0",
            borderLeft: "4px solid #f44336",
            "& .MuiAlert-icon": {
              color: "#f44336",
            },
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            ⚠️ {incompleteTestsCount} test(s) are not yet completed
          </Typography>
          <Typography variant="body2">
            These tests have not reached the "Engineer Approved" stage. Please
            review the highlighted items below.
          </Typography>
        </Alert>
      )}

      {/* Tests Progress Overview */}
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <EngineeringIcon sx={{ fontSize: 32 }} /> Test Progress Overview
        {labRequestData.labRequestTests.length >
          allNonTransportTests.length && (
          <Chip
            icon={<TransportIcon />}
            label={`${labRequestData.labRequestTests.length - allNonTransportTests.length} Transport test(s) hidden`}
            size="medium"
            sx={{ ml: 2, bgcolor: "#f5f5f5", fontWeight: 500 }}
          />
        )}
      </Typography>

      {allNonTransportTests.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No non-transport tests found for this lab request.
        </Alert>
      ) : (
        allNonTransportTests.map((test, index) => {
          const progress = calculateProgress(test);
          const statusInfo = getStatusInfo(test);
          const testName = test.labTest?.testName || "Unknown Test";
          const uniqueId = test.id
            ? `test-${test.id}`
            : `test-${index}-${testName}`;
          const isExpanded = expandedTests.has(uniqueId);
          const isIncomplete = !test.checkedByEngineer;

          // Extract personnel information (these are objects)
          const testedBy = test.testedBy;
          const reportedBy = test.reportedBy;
          const assignUser = test.assignUser;
          const preparedBy = test.preparedBy;

          return (
            <StyledCard key={uniqueId} incomplete={isIncomplete}>
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1.25rem",
                          color: isIncomplete ? "#f44336" : "inherit",
                        }}
                      >
                        {testName}
                      </Typography>
                      {isIncomplete && (
                        <Tooltip title="Test not completed yet">
                          <WarningIcon
                            sx={{ color: "#f44336", fontSize: 20 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <StatusChip
                      label={statusInfo.label}
                      icon={statusInfo.icon}
                      statuscolor={statusInfo.status}
                    />
                  </Box>
                }
                subheader={
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1, fontSize: "0.9rem" }}
                  >
                    <strong>Quantity:</strong> {test.quantity} |{" "}
                    <strong>Total Amount:</strong> Rs.{" "}
                    {test.total?.toLocaleString()}
                    {test.castingDate &&
                      ` | Casting Date: ${formatDate(test.castingDate)}`}
                    {test.testDate &&
                      ` | Test Date: ${formatDate(test.testDate)}`}
                  </Typography>
                }
                action={
                  <Tooltip title={isExpanded ? "Hide Details" : "View Details"}>
                    <IconButton
                      onClick={() => handleAccordionToggle(uniqueId)}
                      sx={{
                        bgcolor: isExpanded ? statusInfo.color : "transparent",
                        color: isExpanded ? "white" : statusInfo.color,
                        "&:hover": {
                          bgcolor: statusInfo.color,
                          color: "white",
                        },
                      }}
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: isExpanded ? "rotate(180deg)" : "none",
                          transition: "transform 0.3s",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                }
              />
              <CardContent>
                {/* Incomplete Warning Banner for Individual Test */}
                {isIncomplete && (
                  <IncompleteWarning>
                    <WarningIcon sx={{ color: "#f44336" }} />
                    <Typography
                      variant="body2"
                      sx={{ color: "#c62828", fontWeight: 500 }}
                    >
                      ⚠️ This test is incomplete. All three steps need to be
                      completed for final approval.
                    </Typography>
                  </IncompleteWarning>
                )}

                {/* Personnel Information Section */}
                <Paper
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <PersonIcon sx={{ color: "#667eea" }} />
                    Personnel Assigned
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Tested By */}
                    <Grid item xs={12} sm={6} md={3}>
                      <PersonnelCard>
                        {renderUserInfo(
                          testedBy,
                          <VerifiedUserIcon
                            sx={{ color: "#4caf50", fontSize: 20 }}
                          />,
                        )}
                        {!testedBy && (
                          <Typography variant="body2" color="textSecondary">
                            Not assigned yet
                          </Typography>
                        )}
                      </PersonnelCard>
                    </Grid>

                    {/* Reported By */}
                    <Grid item xs={12} sm={6} md={3}>
                      <PersonnelCard>
                        {renderUserInfo(
                          reportedBy,
                          <AssignmentIndIcon
                            sx={{ color: "#2196f3", fontSize: 20 }}
                          />,
                        )}
                        {!reportedBy && (
                          <Typography variant="body2" color="textSecondary">
                            Not assigned yet
                          </Typography>
                        )}
                      </PersonnelCard>
                    </Grid>

                    {/* Assign User */}
                    <Grid item xs={12} sm={6} md={3}>
                      <PersonnelCard>
                        {renderUserInfo(
                          assignUser,
                          <HowToRegIcon
                            sx={{ color: "#ff9800", fontSize: 20 }}
                          />,
                        )}
                        {!assignUser && (
                          <Typography variant="body2" color="textSecondary">
                            Not assigned yet
                          </Typography>
                        )}
                      </PersonnelCard>
                    </Grid>

                    {/* Prepared By */}
                    <Grid item xs={12} sm={6} md={3}>
                      <PersonnelCard>
                        {renderUserInfo(
                          preparedBy,
                          <EngineeringIcon
                            sx={{ color: "#9c27b0", fontSize: 20 }}
                          />,
                        )}
                        {!preparedBy && (
                          <Typography variant="body2" color="textSecondary">
                            Not assigned yet
                          </Typography>
                        )}
                      </PersonnelCard>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Progress Bar */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      fontSize="1rem"
                    >
                      Overall Progress
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        color:
                          progress === 100
                            ? "#4caf50"
                            : isIncomplete
                              ? "#f44336"
                              : "#2196f3",
                      }}
                    >
                      {Math.round(progress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 6,
                        background:
                          progress === 100
                            ? "linear-gradient(90deg, #4caf50, #8bc34a)"
                            : isIncomplete
                              ? "linear-gradient(90deg, #f44336, #ff9800)"
                              : "linear-gradient(90deg, #2196f3, #64b5f6)",
                      },
                    }}
                  />
                </Box>

                {/* Status Indicators with Larger Fonts */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={4}>
                    <StatusIndicator active={test.finalized} color="#ff9800">
                      {test.finalized ? (
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: 32 }}
                        />
                      ) : (
                        <ScienceIcon sx={{ color: "#ff9800", fontSize: 32 }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ fontSize: "1rem", mb: 0.5 }}
                        >
                          Finalized
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          sx={{
                            fontSize: "0.95rem",
                            color: test.finalized ? "#4caf50" : "#ff9800",
                          }}
                        >
                          {test.finalized ? "✓ Completed" : "⏳ Pending"}
                        </Typography>
                        {test.finalizedDateTime && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              mt: 0.5,
                              display: "block",
                            }}
                          >
                            {formatDateTime(test.finalizedDateTime)}
                          </Typography>
                        )}
                      </Box>
                    </StatusIndicator>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatusIndicator active={test.checked} color="#2196f3">
                      {test.checked ? (
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: 32 }}
                        />
                      ) : (
                        <AssignmentIcon
                          sx={{ color: "#2196f3", fontSize: 32 }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ fontSize: "1rem", mb: 0.5 }}
                        >
                          TO Checked
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          sx={{
                            fontSize: "0.95rem",
                            color: test.checked ? "#4caf50" : "#2196f3",
                          }}
                        >
                          {test.checked ? "✓ Completed" : "⏳ Pending"}
                        </Typography>
                        {test.checkedDateTime && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              mt: 0.5,
                              display: "block",
                            }}
                          >
                            {formatDateTime(test.checkedDateTime)}
                          </Typography>
                        )}
                      </Box>
                    </StatusIndicator>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatusIndicator
                      active={test.checkedByEngineer}
                      color="#9c27b0"
                    >
                      {test.checkedByEngineer ? (
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: 32 }}
                        />
                      ) : (
                        <EngineeringIcon
                          sx={{ color: "#9c27b0", fontSize: 32 }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ fontSize: "1rem", mb: 0.5 }}
                        >
                          Engineer Approved
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          sx={{
                            fontSize: "0.95rem",
                            color: test.checkedByEngineer
                              ? "#4caf50"
                              : "#9c27b0",
                          }}
                        >
                          {test.checkedByEngineer
                            ? "✓ Completed"
                            : "⏳ Pending"}
                        </Typography>
                        {test.checkedByEngineerDateTime && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              fontSize: "0.75rem",
                              mt: 0.5,
                              display: "block",
                            }}
                          >
                            {formatDateTime(test.checkedByEngineerDateTime)}
                          </Typography>
                        )}
                      </Box>
                    </StatusIndicator>
                  </Grid>
                </Grid>

                {/* Detailed Steps (Accordion Content) */}
                <Accordion
                  expanded={isExpanded}
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    mt: 2,
                    border: `2px solid ${isIncomplete ? "#f44336" : statusInfo.color}`,
                    borderRadius: 2,
                  }}
                >
                  <AccordionSummary
                    sx={{
                      px: 2,
                      minHeight: "auto",
                      "& .MuiAccordionSummary-content": { my: 1.5 },
                      bgcolor: isIncomplete
                        ? "#ffebee"
                        : `${statusInfo.color}10`,
                    }}
                    onClick={() => handleAccordionToggle(uniqueId)}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        color: isIncomplete ? "#f44336" : "primary",
                      }}
                    >
                      📋 View Detailed Timeline {isIncomplete && "(Incomplete)"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 2, pb: 2 }}>
                    <Box sx={{ mt: 2 }}>
                      {/* Step 1 */}
                      <Box sx={{ display: "flex", mb: 3 }}>
                        <Box sx={{ mr: 2 }}>
                          {getStepIcon(1, test.finalized)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ fontSize: "1rem", mb: 0.5 }}
                          >
                            Step 1: Finalized
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontSize: "0.85rem" }}
                          >
                            Report is ready for Technical Officer review
                          </Typography>
                          {test.finalizedDateTime && (
                            <Box
                              sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: "#fff3e0",
                                borderRadius: 2,
                                borderLeft: `3px solid #ff9800`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                fontWeight={500}
                              >
                                <ScheduleIcon
                                  sx={{ fontSize: 16, color: "#ff9800" }}
                                />
                                Completed on:{" "}
                                {formatDateTime(test.finalizedDateTime)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Step 2 */}
                      <Box sx={{ display: "flex", mb: 3 }}>
                        <Box sx={{ mr: 2 }}>{getStepIcon(2, test.checked)}</Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ fontSize: "1rem", mb: 0.5 }}
                          >
                            Step 2: Technical Officer Check
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontSize: "0.85rem" }}
                          >
                            Verified and checked by Technical Officer
                          </Typography>
                          {test.checkedDateTime && (
                            <Box
                              sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: "#e3f2fd",
                                borderRadius: 2,
                                borderLeft: `3px solid #2196f3`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                fontWeight={500}
                              >
                                <ScheduleIcon
                                  sx={{ fontSize: 16, color: "#2196f3" }}
                                />
                                Completed on:{" "}
                                {formatDateTime(test.checkedDateTime)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Step 3 */}
                      <Box sx={{ display: "flex" }}>
                        <Box sx={{ mr: 2 }}>
                          {getStepIcon(3, test.checkedByEngineer)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ fontSize: "1rem", mb: 0.5 }}
                          >
                            Step 3: Engineer Approval
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontSize: "0.85rem" }}
                          >
                            Final approval by Engineer
                          </Typography>
                          {test.checkedByEngineerDateTime && (
                            <Box
                              sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: "#f3e5f5",
                                borderRadius: 2,
                                borderLeft: `3px solid #9c27b0`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                fontWeight={500}
                              >
                                <ScheduleIcon
                                  sx={{ fontSize: 16, color: "#9c27b0" }}
                                />
                                Completed on:{" "}
                                {formatDateTime(test.checkedByEngineerDateTime)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </StyledCard>
          );
        })
      )}

      {/* Overall Statistics */}
      <Paper
        sx={{
          p: 3,
          mt: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 3, fontWeight: "bold", textAlign: "center" }}
        >
          📊 Overall Statistics (Excluding Transport Tests)
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: "#ff9800",
                  fontWeight: "bold",
                  fontSize: "2.5rem",
                }}
              >
                {allNonTransportTests.filter((t) => t.finalized).length}
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                fontWeight={600}
                sx={{ mt: 1 }}
              >
                Tests Finalized
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  (allNonTransportTests.filter((t) => t.finalized).length /
                    allNonTransportTests.length) *
                  100
                }
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#ffe0b2",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#ff9800" },
                }}
              />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ mt: 1, color: "#ff9800" }}
              >
                {Math.round(
                  (allNonTransportTests.filter((t) => t.finalized).length /
                    allNonTransportTests.length) *
                    100,
                )}
                %
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: "#2196f3",
                  fontWeight: "bold",
                  fontSize: "2.5rem",
                }}
              >
                {allNonTransportTests.filter((t) => t.checked).length}
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                fontWeight={600}
                sx={{ mt: 1 }}
              >
                Tests Checked by TO
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  (allNonTransportTests.filter((t) => t.checked).length /
                    allNonTransportTests.length) *
                  100
                }
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#bbdef5",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#2196f3" },
                }}
              />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ mt: 1, color: "#2196f3" }}
              >
                {Math.round(
                  (allNonTransportTests.filter((t) => t.checked).length /
                    allNonTransportTests.length) *
                    100,
                )}
                %
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: "#4caf50",
                  fontWeight: "bold",
                  fontSize: "2.5rem",
                }}
              >
                {allNonTransportTests.filter((t) => t.checkedByEngineer).length}
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                fontWeight={600}
                sx={{ mt: 1 }}
              >
                Tests Engineer Approved
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  (allNonTransportTests.filter((t) => t.checkedByEngineer)
                    .length /
                    allNonTransportTests.length) *
                  100
                }
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#c8e6c9",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#4caf50" },
                }}
              />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ mt: 1, color: "#4caf50" }}
              >
                {Math.round(
                  (allNonTransportTests.filter((t) => t.checkedByEngineer)
                    .length /
                    allNonTransportTests.length) *
                    100,
                )}
                %
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: "1.1rem" }}
          >
            📈 Total Active Tests: {allNonTransportTests.length} | 🎯 Completion
            Rate:
            <span
              style={{
                color:
                  completionRate >= 80
                    ? "#4caf50"
                    : completionRate >= 50
                      ? "#ff9800"
                      : "#f44336",
                fontSize: "1.3rem",
                fontWeight: "bold",
                marginLeft: "8px",
              }}
            >
              {completionRate}%
            </span>
          </Typography>
          {incompleteTestsCount > 0 && (
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: "#f44336",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <WarningIcon sx={{ fontSize: 18 }} />
              {incompleteTestsCount} incomplete test(s) remaining
            </Typography>
          )}
          {labRequestData.labRequestTests.length >
            allNonTransportTests.length && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              *{" "}
              {labRequestData.labRequestTests.length -
                allNonTransportTests.length}{" "}
              transport test(s) have been excluded from the progress tracking
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default LabRequestProgress;
